import { useMutation, useQuery, useQueryClient } from 'react-query'
import { EstimateStatus, ProjectStatus, ProjectType } from '../../common/constants'
import api from '../api'
import { JobListItem } from './jobListItem'

type JobListRequest = {
  search?: string
  sortColumn?: string
  sortOrder?: 'ascend' | 'descend'
  startDate?: string
  endDate?: string
  statuses: ProjectStatus[]
  estimateStatuses: EstimateStatus[]
  jobType?: ProjectType,
  sold?: boolean
  archived?: boolean
  page: number,
  pageSize: number
}

type JobListResponse = {
  jobs: JobListItem[]
  totalRows: number
  totalFinalPrice: number
}

export const getJobs = async (request: JobListRequest) => {
  return (await api.get<JobListResponse>('/web/jobs', {
    params: {
      ...request,
      statuses: request.statuses.join(','),
      estimateStatuses: request.estimateStatuses.join(',')
    }
  })).data
}

export const getDeletedJobs = async (request: JobListRequest) => {
  return (await api.get<JobListResponse>('/web/jobs/deleted', {
    params: request
  })).data
}

export const restoreJob = async (id: number) => {
  await api.post(`/projects/restore/${id}`)
}

export const useJobColumn = (request: JobListRequest, enabled = true) => {
  return useQuery(['job-list-column', request], {
    queryFn: () => getJobs(request),
    keepPreviousData: true,
    enabled
  })
}

export const useJobs = (request: JobListRequest, deleted: boolean) => {
  return useQuery(['job-list', { ...request, deleted }], {
    queryFn: () => (deleted ? getDeletedJobs(request) : getJobs(request)),
    keepPreviousData: true
  })
}

export const useJobListInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidate: () => queryClient.invalidateQueries(['job-list'])
  }
}

export const useJobColumnInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidate: () => queryClient.invalidateQueries(['job-list-column'])
  }
}

export const useRestoreJobMutation = (onSuccess: (item: JobListItem) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (item: JobListItem) => await restoreJob(item.id),
    onSuccess: (_, item: JobListItem) => {
      queryClient.invalidateQueries(['job-list'])
      onSuccess(item)
    }
  })
}
